import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Boxes, Save, Search, X, AlertCircle } from "lucide-react";

/**
 * Bulk table to assign platforms (Idealista Pro, Confidencial, Fizbot, ...)
 * to consultants. Rows = consultants, columns = platforms. Pending changes are
 * tracked locally and saved in batch (one PUT request per modified consultant).
 *
 * Access: admin or staff with `is_platforms_manager` flag.
 */
export default function PlatformsAssign() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || (user?.role === "staff" && user?.is_platforms_manager);

  const [players, setPlayers] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  // Pending state: { playerId: Set<platformId> } only for rows the user touched
  const [pending, setPending] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [ps, pl] = await Promise.all([api.get("/players"), api.get("/platforms")]);
        setPlayers(ps.data.filter((p) => p.is_active !== false));
        setPlatforms(pl.data);
      } catch (err) { toast.error(formatApiError(err)); }
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = players;
    if (needle) {
      list = list.filter((p) =>
        `${p.name || ""} ${p.email || ""} ${p.club || ""} ${p.coach_mbti || ""}`.toLowerCase().includes(needle));
    }
    return [...list].sort((a, b) => {
      const c = (a.club || "zz").localeCompare(b.club || "zz", "pt");
      return c !== 0 ? c : (a.name || "").localeCompare(b.name || "", "pt");
    });
  }, [players, q]);

  const currentIds = (player) => {
    if (pending[player.id]) return pending[player.id];
    return new Set(player.platform_ids || []);
  };

  const toggle = (player, platformId) => {
    setPending((prev) => {
      const next = { ...prev };
      const set = new Set(next[player.id] || player.platform_ids || []);
      if (set.has(platformId)) set.delete(platformId); else set.add(platformId);
      next[player.id] = set;
      return next;
    });
  };

  const dirtyCount = Object.keys(pending).length;

  const saveAll = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    let ok = 0, fail = 0;
    const updates = Object.entries(pending);
    for (const [pid, set] of updates) {
      try {
        await api.put(`/players/${pid}/platforms`, { platform_ids: Array.from(set) });
        ok++;
      } catch (_err) { fail++; }
    }
    // Refresh from server
    try {
      const ps = await api.get("/players");
      setPlayers(ps.data.filter((p) => p.is_active !== false));
    } catch (_err) { /* ignore */ }
    setPending({});
    setSaving(false);
    if (fail === 0) toast.success(`${ok} consultor(es) atualizado(s)`);
    else toast.error(`${ok} guardado(s), ${fail} falharam`);
  };

  const discardAll = () => {
    setPending({});
    toast.info("Alterações descartadas");
  };

  if (!canManage) {
    return (
      <div className="kw-card p-6 max-w-xl" data-testid="platforms-assign-forbidden">
        <div className="flex items-center gap-2 text-[#FF6B6B] mb-2">
          <AlertCircle className="w-5 h-5" />
          <span className="font-display font-black uppercase">Acesso negado</span>
        </div>
        <p className="text-sm text-[#A3A3A3]">
          Esta página é apenas para o Admin ou um Staff com o atributo
          <span className="font-mono"> &nbsp;is_platforms_manager </span> ativo.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 kw-fade-up" data-testid="platforms-assign-page">
      <div>
        <div className="kw-overline">Gestão em massa</div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter">
          Plataformas <span className="text-[#E50000]">por Consultor</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          Tabela completa de subscrições. Marca/desmarca as plataformas para cada consultor
          e guarda todas as alterações em lote.
        </p>
      </div>

      <div className="kw-card p-3 flex flex-col md:flex-row gap-3 md:items-center" data-testid="platforms-assign-toolbar">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <input className="kw-input !pl-10 w-full" placeholder="Procurar consultor por nome/clube/MBTI…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="platforms-assign-search" />
        </div>
        {dirtyCount > 0 && (
          <button onClick={discardAll} disabled={saving}
            className="kw-btn-secondary inline-flex items-center gap-1.5"
            data-testid="platforms-assign-discard">
            <X className="w-4 h-4" /> Descartar ({dirtyCount})
          </button>
        )}
        <button onClick={saveAll} disabled={dirtyCount === 0 || saving}
          className="kw-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="platforms-assign-save">
          <Save className="w-4 h-4" /> {saving ? "A guardar…" : `Guardar ${dirtyCount || ""}`.trim()}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#737373]">A carregar…</p>
      ) : platforms.length === 0 ? (
        <div className="kw-card p-6 text-sm text-[#737373] italic">
          Não há plataformas configuradas. Cria-as primeiro em <span className="font-mono">/settings</span>.
        </div>
      ) : (
        <div className="kw-card overflow-x-auto" data-testid="platforms-assign-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="kw-overline border-b border-[#222]">
                <th className="p-3 text-left">Consultor</th>
                <th className="p-3 text-left">Clube</th>
                {platforms.map((pl) => (
                  <th key={pl.id} className="p-3 text-center" style={{ color: pl.color }}>
                    <div className="flex flex-col items-center gap-1">
                      {pl.logo_url && <img src={pl.logo_url} alt="" className="w-5 h-5 object-contain" />}
                      <span>{pl.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const ids = currentIds(p);
                const dirty = Boolean(pending[p.id]);
                return (
                  <tr key={p.id} className={`border-b border-[#161616] hover:bg-[#111] ${dirty ? "bg-[#E50000]/5" : ""}`} data-testid={`pa-row-${p.id}`}>
                    <td className="p-3 font-display font-bold uppercase tracking-tight">
                      {p.name}
                      {dirty && <span className="ml-2 text-[10px] text-[#E50000]">●</span>}
                    </td>
                    <td className="p-3 text-[#A3A3A3] text-xs">{p.club || "—"}</td>
                    {platforms.map((pl) => {
                      const checked = ids.has(pl.id);
                      return (
                        <td key={pl.id} className="p-3 text-center">
                          <input type="checkbox"
                            checked={checked}
                            onChange={() => toggle(p, pl.id)}
                            className="accent-[#E50000] w-4 h-4 cursor-pointer"
                            data-testid={`pa-check-${p.id}-${pl.id}`}
                            aria-label={`${pl.name} para ${p.name}`} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={2 + platforms.length} className="p-6 text-center text-[#737373] italic">
                  Nenhum consultor corresponde à pesquisa.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10px] text-[#737373] font-mono">
        <Boxes className="inline w-3 h-3 mr-1" /> {filtered.length} consultor(es) · {platforms.length} plataforma(s) · alterações em vermelho · guardadas só ao carregar &quot;Guardar&quot;
      </p>
    </div>
  );
}
